
import React, {useEffect, useMemo, useState} from 'react';
import {useTable, useSortBy, useExpanded, useGroupBy} from 'react-table';

import {getHeatMapColorEquivalentOf} from '../../utils/heat-map-colors.js';
import {conditionalCellRenderer, getCellBackgroundColor, isDisplayTooltip} from './table-grid-util.js';

import GenericTooltip from '../generic-tooltip/generic-tooltip.jsx';


function Table({gridKey, data, config}) {
    const {
        configTree: {isAllExpanded = false} = {isAllExpanded: false}
    } = config;

    const [tooltipData, setTooltipData] = useState(null);

    const getIconExpandCollapse = isShowExpand => <i className={`spark-icon-arrow-triangle-${isShowExpand ? 'down' : 'right'} spark-icon--sm spark-icon--fill`}>&nbsp;</i>;

    const getIconAscDesc = isDescending => <i className={`spark-icon-arrow-triangle-${isDescending ? 'down' : 'up'} spark-icon--sm spark-icon--fill`}>&nbsp;</i>;


    const expanderColumn = {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded = true }) => (<span {...getToggleAllRowsExpandedProps()}>{getIconExpandCollapse(isAllRowsExpanded)}</span>),
        Cell: ({ row }) =>
            // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
            // to build the toggle for expanding a row
            row.canExpand ? (
                <span
                    {...row.getToggleRowExpandedProps({
                        style: {
                            // We can even use the row.depth property
                            // and paddingLeft to indicate the depth
                            // of the row
                            paddingLeft: `${row.depth * 0.5}rem`
                        },
                    })}
                >{getIconExpandCollapse(row.isExpanded)}
                </span>
            ) : null,
        thClassName: `table-grid--header-expander`,
        sortable: false
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        toggleAllRowsExpanded,
    } = useTable(
        {
            columns: useMemo(() => [expanderColumn, ...config.configColumns], []),
            data: useMemo(() => data, []),
            autoResetExpanded: false,
        },
        useGroupBy,
        useSortBy,
        useExpanded
    );

    const firstPageRows = rows;
    const heatMapValueOf = getHeatMapColorEquivalentOf(data, config);

    const getTableCss = (isVerticalHeader, additionalCss) => {
        return `table-grid${isVerticalHeader ? ` table-grid--vertical` : ``}${additionalCss ? ` ${additionalCss}` : ''}`;
    };

    const getTableHeaderCss = (isVerticalHeader, additionalCss) => {
        return isVerticalHeader ? `table-grid--header-vertical${additionalCss}` : `table-grid--header${additionalCss}`;
    };

    useEffect(() => {
        if(isAllExpanded) {
            toggleAllRowsExpanded(true);
        }
    }, [config, data]);

    return (
        <div className={`${config.isVerticalHeader ? '' : 'table-wrap'}`}>
            <table key={gridKey} {...getTableProps()} className={getTableCss(config.isVerticalHeader, config.tableClassName)}>
                <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                className={getTableHeaderCss(config.isVerticalHeader && column.id !== 'expander', column.thClassName ? ` ${column.thClassName}` : '')}
                            ><p>
                                <span>{column.render('Header')}</span>
                                <span>{column.isSorted && getIconAscDesc(column.isSortedDesc)}</span>
                            </p>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {firstPageRows.map((row, i) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map((cell, j) => {
                                const isEnabledTooltip = isDisplayTooltip(cell);
                                const tooltipProps = isEnabledTooltip ? {'data-tip': true, 'data-for': 'table-data', 'onMouseOver': () => {
                                    setTooltipData({tooltipTitle: row.original.tooltipTitle, tooltipDetails: row.cells});
                                }} : null;

                                return <td
                                    {...tooltipProps}
                                    {...cell.getCellProps()}
                                    className={`table-grid--data${cell.column.tdClassName ? ` ${cell.column.tdClassName}` : ''}`}
                                    style={{'background-color': getCellBackgroundColor(config, cell, heatMapValueOf)}}>
                                    {cell.isGrouped ?
                                        (
                                            // If it's a grouped cell, add an expander and row count
                                            <>
                                                <span {...row.getToggleRowExpandedProps()}>{getIconExpandCollapse(row.isExpanded)}</span>{' '}
                                                {conditionalCellRenderer(config, cell)}{config.configExpandable && config.configExpandable.isShowChildCount ?
                                                ` [${row.subRows.length}${config.configExpandable.childCountLabel ? ` ${config.configExpandable.childCountLabel}` : ''}]` : ''}
                                            </>
                                        ) : cell.isAggregated ? (
                                            // If the cell is aggregated, use the Aggregated
                                            // renderer for cell
                                            cell.render('Aggregated')
                                            // cellRenderer
                                        ) : (cell.isPlaceholder ? null :
                                            // For cells with repeated values, render null
                                            // Otherwise, just render the regular cell
                                            conditionalCellRenderer(config, cell))
                                    }
                                </td>
                            })}
                        </tr>
                    )
                })}
                </tbody>
            </table>
            <GenericTooltip tooltipId={`table-data`} tooltipData={tooltipData} />
        </div>
    )
}

const TableGrid = ({gridKey, data, config}) => {

    return <Table gridKey={gridKey} data={data} config={config}/>;
};

TableGrid.propTypes = {};

export default TableGrid;
